import { useEffect, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';

const money = (cents: number) => (cents / 100).toFixed(2);

export default function Settings() {
  const me = useQuery(api.orgs.current, {});
  const members = useQuery(api.members.list, {});
  const rules = useQuery(api.intervals.list, {});
  const updateBranding = useMutation(api.orgs.updateBranding);
  const xero = useQuery(api.xero.status, {});
  const connectXero = useAction(api.xero.connectUrl);
  const disconnectXero = useMutation(api.xero.disconnect);
  const setRates = useMutation(api.invoices.setRates);
  const [params] = useSearchParams();

  const [tradingName, setTradingName] = useState('');
  const [abn, setAbn] = useState('');
  const [brandColor, setBrandColor] = useState('#0f766e');
  const [saved, setSaved] = useState(false);
  const [callout, setCallout] = useState('');
  const [perItem, setPerItem] = useState('');
  const [ratesSaved, setRatesSaved] = useState(false);
  const [xeroBusy, setXeroBusy] = useState(false);

  useEffect(() => {
    if (!me) return;
    setTradingName(me.org.tradingName);
    setAbn(me.org.abn ?? '');
    setBrandColor(me.org.brandColor ?? '#0f766e');
    setCallout(me.org.calloutFeeCents ? money(me.org.calloutFeeCents) : '');
    setPerItem(me.org.perItemCents ? money(me.org.perItemCents) : '');
  }, [me]);

  if (me === undefined) return <p className="muted">Loading…</p>;

  const unverified = (rules ?? []).some((r) => r.version.includes('unverified'));

  return (
    <>
      <h1>Settings</h1>

      <h2>Your branding</h2>
      <div className="card">
        <p className="muted small">
          This is what your clients see on their portal and on every report — your name, not ours.
        </p>
        <label>Trading name</label>
        <input value={tradingName} onChange={(e) => setTradingName(e.target.value)} />
        <label>ABN</label>
        <input value={abn} onChange={(e) => setAbn(e.target.value)} placeholder="12 345 678 901" />
        <label>Accent colour</label>
        <input value={brandColor} onChange={(e) => setBrandColor(e.target.value)} placeholder="#0f766e" />
        <div style={{ marginTop: '.75rem' }}>
          <button
            onClick={async () => {
              await updateBranding({ tradingName, abn, brandColor });
              setSaved(true);
              setTimeout(() => setSaved(false), 2000);
            }}
          >
            {saved ? 'Saved' : 'Save branding'}
          </button>
        </div>
      </div>

      <h2>Charge-out rates</h2>
      <div className="card">
        <p className="muted small">
          Used to draft an invoice when you complete a visit. You can edit every invoice
          before issuing it — these are a starting point, not a commitment.
        </p>
        <label>Attendance / callout fee (ex GST)</label>
        <input value={callout} onChange={(e) => setCallout(e.target.value)} placeholder="90.00" />
        <label>Per item tested (ex GST)</label>
        <input value={perItem} onChange={(e) => setPerItem(e.target.value)} placeholder="3.50" />
        <div style={{ marginTop: '.75rem' }}>
          <button
            onClick={async () => {
              await setRates({
                calloutFeeCents: Math.round(Number(callout || 0) * 100),
                perItemCents: Math.round(Number(perItem || 0) * 100),
                gstRate: 0.1,
              });
              setRatesSaved(true);
              setTimeout(() => setRatesSaved(false), 2000);
            }}
          >
            {ratesSaved ? 'Saved' : 'Save rates'}
          </button>
        </div>
      </div>

      <h2>Xero</h2>
      <div className="card">
        {params.get('xero') === 'connected' ? (
          <p className="small" style={{ color: 'var(--ok)' }}>Xero connected.</p>
        ) : null}
        {params.get('xero') === 'error' ? (
          <p className="small" style={{ color: 'var(--alert)' }}>
            Could not connect Xero ({params.get('reason')}). Try again.
          </p>
        ) : null}

        {xero?.connected ? (
          <>
            <p className="small">
              Connected{xero.tenantId ? '' : ' — but no Xero organisation is linked yet'}.
              {xero.lastError ? ` Last error: ${xero.lastError}` : ''}
            </p>
            <button className="danger" onClick={() => void disconnectXero({})}>Disconnect Xero</button>
          </>
        ) : (
          <>
            <p className="muted small">
              Push issued invoices to Xero as drafts. Your accountant approves them there —
              TagVault never authorises anything or touches your ledgers.
            </p>
            <button
              disabled={xeroBusy}
              onClick={async () => {
                setXeroBusy(true);
                try {
                  const { url } = await connectXero({
                    redirectUri: `${import.meta.env.VITE_CONVEX_SITE_URL}/oauth/xero/callback`,
                  });
                  window.location.href = url;
                } finally {
                  setXeroBusy(false);
                }
              }}
            >
              {xeroBusy ? 'Opening Xero…' : 'Connect Xero'}
            </button>
          </>
        )}
      </div>

      <h2>Retest intervals</h2>
      <div className="card">
        {unverified ? (
          <p className="small" style={{ color: 'var(--alert)' }}>
            <strong>These intervals have not been checked against the published standard.</strong>{' '}
            They are seed values so the app has something to work with. Verify every row against
            AS/NZS 3760:2022 Table 4 before recording real tests.
          </p>
        ) : null}
        <table>
          <thead><tr><th>Environment</th><th>Interval</th><th>Jurisdiction</th><th>Version</th></tr></thead>
          <tbody>
            {(rules ?? []).map((r) => (
              <tr key={r._id}>
                <td>{r.environmentClass.replace(/_/g, ' ')}</td>
                <td>{r.months} months</td>
                <td className="muted">{r.jurisdiction ?? 'all'}</td>
                <td className="muted small">{r.version}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small" style={{ marginTop: '.75rem' }}>
          Intervals are data, not code. Correcting them — or absorbing a revision of the
          standard — takes effect immediately, with no app update. Every past test keeps the
          rule version that produced its due date.
        </p>
      </div>

      <h2>Technicians</h2>
      <div className="card">
        <table>
          <thead><tr><th>Name</th><th>Role</th><th>Competency</th><th>Status</th></tr></thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m._id}>
                <td>{m.displayName}</td>
                <td className="muted">{m.role}</td>
                <td className="muted small">{m.competencyRef ?? '—'}</td>
                <td>{m.active ? 'active' : <span className="pill">inactive</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteAccount businessName={me?.org.tradingName} />
    </>
  );
}

const date = (ms: number) => new Date(ms).toLocaleDateString('en-AU');

/**
 * The web half of account deletion. Google Play requires a way to request deletion without
 * the app installed; this is it, and the mobile app calls the same backend. It asks rather
 * than erases — see convex/account.ts for why records are not destroyed on a click.
 */
function DeleteAccount({ businessName }: { businessName?: string }) {
  const status = useQuery(api.account.deletionStatus, {});
  const request = useMutation(api.account.requestDeletion);
  const withdraw = useMutation(api.account.cancelDeletion);
  const [busy, setBusy] = useState(false);

  if (!status) return null;
  const owner = status.scope === 'business';
  const name = businessName ?? 'this business';

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 id="delete-account">Delete account</h2>
      <div className="card">
        {status.pending ? (
          <>
            <p>Deletion requested on {date(status.pending.requestedAt)}.</p>
            <p className="muted">
              It will be completed by {date(status.pending.dueBy)}. Everything keeps working
              until then, and you can withdraw the request.
            </p>
            <button className="quiet" disabled={busy} onClick={() => void run(() => withdraw({}))}>
              Withdraw request
            </button>
          </>
        ) : (
          <>
            <p>
              {owner
                ? `This closes ${name} and deletes its records for everyone in it.`
                : `This removes your access to ${name} and your personal details.`}
            </p>
            <p className="muted">
              {owner ? (
                <>
                  That includes the register and test history your clients may need as evidence
                  — <Link to="/exports">export them first</Link>.{' '}
                </>
              ) : (
                'Tests you performed keep your name on them, because a test record without its technician is not evidence. '
              )}
              Deletion is completed within 30 days rather than immediately, so there is time to
              export and to change your mind. Billing records are kept where tax law requires.
            </p>
            <button
              className="danger"
              disabled={busy}
              onClick={() => {
                const ok = window.confirm(
                  owner
                    ? `Close ${name}? All of its records will be deleted within 30 days.`
                    : `Leave ${name}? Your access and personal details will be removed within 30 days.`,
                );
                if (ok) void run(() => request({}));
              }}
            >
              Request deletion
            </button>
          </>
        )}
      </div>
    </>
  );
}
